import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/common/Widgets";
import { CheckCircle2, XCircle, AlertTriangle, MinusCircle, Banknote, Loader2, FileCheck2 } from "lucide-react";

const ICONS = { PASS: CheckCircle2, FAIL: XCircle, WARN: AlertTriangle, "N/A": MinusCircle };
const COLORS = { PASS: "text-success", FAIL: "text-destructive", WARN: "text-warning", "N/A": "text-muted-foreground" };
const BG = { PASS: "bg-success/5 border-success/20", FAIL: "bg-destructive/5 border-destructive/20", WARN: "bg-warning/5 border-warning/20", "N/A": "bg-muted/20 border-border" };

export default function DisbursementSection({ app, reload }) {
  const [check, setCheck] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initiating, setInitiating] = useState(false);
  const [showInit, setShowInit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        api.get(`/disbursement/${app.id}/checklist`),
        api.get("/disbursement", { params: { application_id: app.id } }),
      ]);
      setCheck(c.data); setHistory(h.data);
    } catch (e) { toast.error("Failed to load checklist"); }
    finally { setLoading(false); }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="disbursement-section">
      <SectionHeader
        title="Disbursement"
        subtitle="All conditions must clear before money moves. Once disbursed, the application is closed."
        action={check && check.can_disburse && history.length === 0 && (
          <Button onClick={() => setShowInit(true)} data-testid="disbursement-initiate-button"><Banknote className="h-4 w-4 mr-1" />Initiate Disbursement</Button>
        )}
      />

      {loading ? (
        <Card className="p-4 space-y-2">{[0,1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>
      ) : check && (
        <Card className={`p-4 border-2 ${check.passed ? "border-success/30 bg-success/5" : "border-destructive/30 bg-destructive/5"}`} data-testid="disbursement-checklist">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {check.passed ? <CheckCircle2 className="h-5 w-5 text-success" /> : <XCircle className="h-5 w-5 text-destructive" />}
              <span className="font-semibold heading text-sm">{check.passed ? "Ready to disburse" : "Disbursement blocked"}</span>
            </div>
            <Badge variant="outline" className="text-[10px]">{check.items?.length || 0} checks</Badge>
          </div>
          <div className="space-y-1.5">
            {check.items?.map((it, i) => {
              const Icon = ICONS[it.status] || MinusCircle;
              return (
                <div key={i} className={`flex items-start gap-2 rounded-md border p-2 ${BG[it.status]}`} data-testid={`check-${it.check}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${COLORS[it.status]}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide mono">{it.check}</span>
                      <Badge variant="outline" className={`text-[10px] ${COLORS[it.status]} border-current/30`}>{it.status}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{it.msg}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-muted/30"><div className="flex items-center gap-2"><FileCheck2 className="h-4 w-4 text-success" /><span className="text-sm font-semibold heading">Disbursement History</span></div><Badge variant="outline" className="text-[10px]">{history.length}</Badge></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Amount</th><th className="text-left px-3 py-2">Mode</th><th className="text-left px-3 py-2">UTR</th><th className="text-left px-3 py-2">Account</th><th className="text-left px-3 py-2">By</th><th className="text-left px-3 py-2">Status</th></tr></thead>
              <tbody>{history.map(d => (<tr key={d.id} className="border-t border-border">
                <td className="px-3 py-1.5 mono text-xs text-muted-foreground">{(d.initiated_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-3 py-1.5 tabular font-semibold">₹ {Number(d.amount_lakhs).toFixed(2)} L</td>
                <td className="px-3 py-1.5 text-xs"><Badge variant="outline" className="text-[10px]">{d.mode}</Badge></td>
                <td className="px-3 py-1.5 mono text-xs">{d.utr}</td>
                <td className="px-3 py-1.5 mono text-xs">{d.customer_account_no || "—"}</td>
                <td className="px-3 py-1.5 text-xs">{d.initiated_by}</td>
                <td className="px-3 py-1.5"><Badge variant="outline" className={`text-[10px] ${d.status === "SUCCESS" ? "text-success border-success/40" : "text-warning border-warning/40"}`}>{d.status}</Badge></td>
              </tr>))}</tbody>
            </table>
          </div>
        </Card>
      )}

      {showInit && <InitiateDialog app={app} onClose={() => setShowInit(false)} onDone={() => { setShowInit(false); load(); reload && reload(); }} />}
    </div>
  );
}

function InitiateDialog({ app, onClose, onDone }) {
  const amt = app.cc_proposed || app.tl_proposed || app.personal_loan_amount || app.housing_loan_amount || 0;
  const [form, setForm] = useState({ amount_lakhs: amt, mode: "NEFT", customer_account_no: "", customer_ifsc: "", customer_bank_name: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/disbursement/initiate", { application_id: app.id, ...form, amount_lakhs: Number(form.amount_lakhs) });
      toast.success("Disbursement initiated successfully");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disbursement failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Initiate Disbursement</DialogTitle><DialogDescription>Mocked NEFT/RTGS push to CBS/LMS. Generates a UTR + sends customer notification.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Amount (₹ L) *</Label><Input type="number" step="0.01" value={form.amount_lakhs} onChange={e => set("amount_lakhs", e.target.value)} data-testid="disb-amount" /></div>
          <div className="space-y-1.5"><Label>Mode *</Label><Select value={form.mode} onValueChange={v => set("mode", v)}><SelectTrigger data-testid="disb-mode"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="NEFT">NEFT</SelectItem><SelectItem value="RTGS">RTGS</SelectItem><SelectItem value="IMPS">IMPS</SelectItem><SelectItem value="CHEQUE">Cheque</SelectItem></SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Account No.</Label><Input value={form.customer_account_no} onChange={e => set("customer_account_no", e.target.value)} /></div>
          <div className="space-y-1.5"><Label>IFSC</Label><Input value={form.customer_ifsc} onChange={e => set("customer_ifsc", e.target.value)} placeholder="SBIN0001234" /></div>
          <div className="space-y-1.5 col-span-2"><Label>Customer Bank Name</Label><Input value={form.customer_bank_name} onChange={e => set("customer_bank_name", e.target.value)} /></div>
          <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.amount_lakhs} data-testid="disb-submit">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Banknote className="h-4 w-4 mr-1" />}Initiate ₹ {Number(form.amount_lakhs || 0).toFixed(2)} L</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
